// Compare page — metric definitions + pure computation helpers.
//
// Keys are the stat keys of GET /dashboard/players/public/{id}/stats/
// (blocks[].stats[].key). For the current TheSports season those are the
// TSSeasonPlayerStat field names (backend dashboard/views.py, stats action);
// historical SportMonks seasons derive their keys from SMType names, so many
// of the keys below simply don't exist there — per the brief's data rules a
// missing value renders as "—" (em-dash), never "n/a", and its bar is omitted.
//
// All formulas follow TP_COMPARE_REBUILD_BRIEF.md verbatim:
//   - every p90 value requires minutes_played >= 90, otherwise "—"
//   - percentages with denominator 0 -> "—"
//   - Min/Goal with goals 0 -> "—"
//   - Yellow = yellow_cards + yellow2red_cards
//   - Duels Air/Ground splits (aerial_won/aerial_lost/ground_won/ground_lost)
//     are delivered by the backend since PR #357 (Defence block, TheSports
//     sync); players whose season rows lack the fields still render "—".
//   - GK Overview has NO clean-sheets row (field doesn't exist; never fake
//     a metric).

import { formatGroupedNumber } from "../../utils/currencyFormatter";

export const EM_DASH = "—";

// ---------------------------------------------------------------------------
// numeric access
// ---------------------------------------------------------------------------

const toNumber = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "object") {
    // SportMonks entries occasionally carry {total|average|goals|scored}.
    for (const k of ["total", "average", "goals", "scored"]) {
      if (typeof v[k] === "number" && Number.isFinite(v[k])) return v[k];
    }
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Flatten a stats entry's blocks[].stats[] into a { key: number } map. */
export const buildStatMap = (entry) => {
  const map = {};
  (entry?.blocks || []).forEach((block) => {
    (block?.stats || []).forEach((s) => {
      if (!s || !s.key) return;
      const v = toNumber(s.value);
      if (v !== null && !(s.key in map)) map[s.key] = v;
    });
  });
  return map;
};

// ---------------------------------------------------------------------------
// value formulas (each returns number | null; null renders as "—")
// ---------------------------------------------------------------------------

const stat = (key) => (s) => (s[key] === undefined ? null : s[key]);

// Yellow = yellow_cards + yellow2red_cards (brief). Both missing -> null.
const sumStats = (a, b) => (s) => {
  const x = s[a] === undefined ? null : s[a];
  const y = s[b] === undefined ? null : s[b];
  if (x === null && y === null) return null;
  return (x || 0) + (y || 0);
};

// num/den × 100; denominator missing or 0 -> null.
const pctOf = (numKey, denKey) => (s) => {
  const n = s[numKey];
  const d = s[denKey];
  if (n === undefined || d === undefined || !(d > 0)) return null;
  return (n / d) * 100;
};

// won/(won+lost) × 100 for the duel splits; either field absent -> null.
const pctWonLost = (wonKey, lostKey) => (s) => {
  const w = s[wonKey];
  const l = s[lostKey];
  if (w === undefined || l === undefined) return null;
  const den = w + l;
  if (!(den > 0)) return null;
  return (w / den) * 100;
};

// value / minutes × 90 with the hard minutes_played >= 90 guard.
const per90Of = (key) => (s) => {
  const v = s[key];
  const m = s.minutes_played;
  if (v === undefined || m === undefined || m < 90) return null;
  return (v / m) * 90;
};

const minPerGoal = (s) => {
  const m = s.minutes_played;
  const g = s.goals;
  if (m === undefined || g === undefined || !(g > 0)) return null;
  return m / g;
};

/**
 * Rating hero: Ø rating per match, one decimal, target range ~0–10.
 *
 * Brief formula: rating / court / 100. BUT the stats endpoint already divides
 * raw values > 10 by 100 before responding (dashboard/views.py, rating
 * conversion inside convert_ts_stats_to_array), so the value arriving here is
 * usually the SUM of match ratings (e.g. 223.2 over 31 matches), sometimes an
 * already-per-match value (<= 10). Defensive normalisation:
 *   - court (matches played) missing/0 -> null
 *   - value > 1000: the endpoint's pre-division was skipped -> divide by 100
 *   - value > 10:   a summed rating -> divide by court
 *   - value <= 10:  already per match -> keep
 *   - anything outside (0, 10] after that is not a plausible rating -> null
 * (Rating is currently NULL for most players — known backend defect F1 — in
 * which case this returns null and the hero shows "—".)
 */
export const computePerMatchRating = (rating, court) => {
  const r = toNumber(rating);
  const c = toNumber(court);
  if (r === null || c === null || !(c > 0)) return null;
  let total = r;
  if (total > 1000) total = total / 100;
  const perMatch = total > 10 ? total / c : total;
  if (!Number.isFinite(perMatch) || perMatch <= 0 || perMatch > 10) return null;
  return Math.round(perMatch * 10) / 10;
};

// ---------------------------------------------------------------------------
// metric definitions
// ---------------------------------------------------------------------------
// Row shape:
//   id        stable id
//   label     English UI label (i18n key — the DOM scanner translates it)
//   note      small suffix next to the label ("p90")
//   fmt       'int' | 'dec1' | 'pct1'
//   value     (statMap) => number|null
//   sub       (statMap) => number|null — a companion percentage, shown as
//             an integer % next to the value (brief: "ganzzahlig %")
//   lower     lower value wins (Min/Goal, Big chances missed, Discipline)
//   extra     hidden behind the block's "Show all stats" expander
//   per90able the block's Total//90 toggle converts this count to per-90
//   statKey   raw key used for the per-90 conversion

export const FIELD_BLOCKS = [
  {
    id: "overview",
    title: "Overview",
    rows: [
      { id: "matches", label: "Matches", fmt: "int", value: stat("matches") },
      { id: "starts", label: "Starts", fmt: "int", value: stat("first") },
      { id: "minutes", label: "Minutes", fmt: "int", value: stat("minutes_played") },
      { id: "goals", label: "Goals", fmt: "int", value: stat("goals") },
      { id: "assists", label: "Assists", fmt: "int", value: stat("assists") },
      { id: "minPerGoal", label: "Min / Goal", fmt: "int", value: minPerGoal, lower: true },
    ],
  },
  {
    id: "attacking",
    title: "Attacking",
    p90: true,
    rows: [
      { id: "shots", label: "Shots", fmt: "int", value: stat("shots"), per90able: true, statKey: "shots" },
      {
        id: "sot",
        label: "Shots on target",
        fmt: "int",
        value: stat("shots_on_target"),
        sub: pctOf("shots_on_target", "shots"),
        per90able: true,
        statKey: "shots_on_target",
      },
      { id: "bcc", label: "Big chances created", fmt: "int", value: stat("big_chance_created"), per90able: true, statKey: "big_chance_created" },
      { id: "bcm", label: "Big chances missed", fmt: "int", value: stat("big_chance_missed"), lower: true, per90able: true, statKey: "big_chance_missed" },
      {
        id: "dribbles",
        label: "Dribbles",
        fmt: "int",
        value: stat("dribble"),
        sub: pctOf("dribble_succ", "dribble"),
        per90able: true,
        statKey: "dribble",
      },
      { id: "keyPasses", label: "Key passes", note: "p90", fmt: "dec1", value: per90Of("key_passes") },
      { id: "fkGoals", label: "Freekick goals", fmt: "int", value: stat("freekick_goals"), extra: true },
    ],
  },
  {
    id: "passing",
    title: "Passing",
    p90: true,
    rows: [
      // Brief: Passes p90 integer ("ganzzahlig").
      { id: "passes", label: "Passes", note: "p90", fmt: "int", value: per90Of("passes") },
      { id: "passAcc", label: "Pass accuracy", fmt: "pct1", value: pctOf("passes_accuracy", "passes") },
      {
        id: "crosses",
        label: "Crosses",
        fmt: "int",
        value: stat("crosses"),
        sub: pctOf("crosses_accuracy", "crosses"),
        per90able: true,
        statKey: "crosses",
      },
      {
        id: "longBalls",
        label: "Long balls",
        fmt: "int",
        value: stat("long_balls"),
        sub: pctOf("long_balls_accuracy", "long_balls"),
        per90able: true,
        statKey: "long_balls",
      },
    ],
  },
  {
    id: "defending",
    title: "Defending",
    rows: [
      { id: "tackles", label: "Tackles", fmt: "int", value: stat("tackles") },
      { id: "interceptions", label: "Interceptions", fmt: "int", value: stat("interceptions") },
      { id: "clearances", label: "Clearances", fmt: "int", value: stat("clearances") },
      { id: "blocked", label: "Blocked shots", fmt: "int", value: stat("blocked_shots") },
      { id: "duels", label: "Duels won", fmt: "pct1", value: pctOf("duels_won", "duels") },
      // Duel splits ship from the backend since PR #357; rows without the
      // fields (older/unsynced season rows) still show "—".
      { id: "aerial", label: "Aerial duels won", fmt: "pct1", value: pctWonLost("aerial_won", "aerial_lost"), extra: true },
      { id: "ground", label: "Ground duels won", fmt: "pct1", value: pctWonLost("ground_won", "ground_lost"), extra: true },
    ],
  },
  {
    id: "discipline",
    title: "Discipline",
    rows: [
      { id: "yellow", label: "Yellow cards", fmt: "int", value: sumStats("yellow_cards", "yellow2red_cards"), lower: true, extra: true },
      { id: "red", label: "Red cards", fmt: "int", value: stat("red_cards"), lower: true, extra: true },
      { id: "fouls", label: "Fouls", fmt: "int", value: stat("fouls"), lower: true, extra: true },
      { id: "wasFouled", label: "Was fouled", fmt: "int", value: stat("was_fouled"), extra: true },
      { id: "offsides", label: "Offsides", fmt: "int", value: stat("offsides"), lower: true, extra: true },
    ],
  },
];

export const GK_BLOCKS = [
  {
    id: "overview",
    title: "Overview",
    // No clean-sheets row: the field does not exist in the stats payload.
    rows: [
      { id: "matches", label: "Matches", fmt: "int", value: stat("matches") },
      { id: "starts", label: "Starts", fmt: "int", value: stat("first") },
      { id: "minutes", label: "Minutes", fmt: "int", value: stat("minutes_played") },
    ],
  },
  {
    id: "goalkeeping",
    title: "Goalkeeping",
    rows: [
      { id: "saves", label: "Saves", fmt: "int", value: stat("saves") },
      { id: "punches", label: "Punches", fmt: "int", value: stat("punches") },
      {
        id: "runsOut",
        label: "Runs out",
        fmt: "int",
        value: stat("runs_out"),
        sub: pctOf("runs_out_succ", "runs_out"),
      },
      { id: "highClaims", label: "High claims", fmt: "int", value: stat("good_high_claim") },
    ],
  },
  {
    id: "distribution",
    title: "Distribution",
    p90: true,
    rows: [
      { id: "passes", label: "Passes", note: "p90", fmt: "int", value: per90Of("passes") },
      { id: "passAcc", label: "Pass accuracy", fmt: "pct1", value: pctOf("passes_accuracy", "passes") },
      {
        id: "longBalls",
        label: "Long balls",
        fmt: "int",
        value: stat("long_balls"),
        sub: pctOf("long_balls_accuracy", "long_balls"),
        per90able: true,
        statKey: "long_balls",
      },
    ],
  },
  {
    id: "discipline",
    title: "Discipline",
    rows: [
      { id: "yellow", label: "Yellow cards", fmt: "int", value: sumStats("yellow_cards", "yellow2red_cards"), lower: true, extra: true },
      { id: "red", label: "Red cards", fmt: "int", value: stat("red_cards"), lower: true, extra: true },
    ],
  },
];

// Mixed GK + field selection: only the shared Overview metrics (frame 1e).
export const MIXED_BLOCKS = [
  {
    id: "overview",
    title: "Overview · shared metrics only",
    rows: [
      { id: "matches", label: "Matches", fmt: "int", value: stat("matches") },
      { id: "starts", label: "Starts", fmt: "int", value: stat("first") },
      { id: "minutes", label: "Minutes", fmt: "int", value: stat("minutes_played") },
    ],
  },
];

export const getBlockDefs = (mode) => {
  if (mode === "gk") return GK_BLOCKS;
  if (mode === "mixed") return MIXED_BLOCKS;
  return FIELD_BLOCKS;
};

// ---------------------------------------------------------------------------
// mode detection
// ---------------------------------------------------------------------------

const GK_ROLE_CODES = new Set(["GK", "GKP", "Goalkeeper"]);

/** Role from the public profile (Wyscout) OR TheSports position "G" from stats. */
export const isGoalkeeper = (profile, statsEntries) => {
  const role = profile?.player?.role || profile?.role || null;
  if ([role?.code2, role?.code3, role?.name].some((c) => c && GK_ROLE_CODES.has(c))) {
    return true;
  }
  return (statsEntries || []).some((e) => {
    const pos = String(e?.player?.position || "").toUpperCase();
    return pos === "G" || pos === "GK" || pos === "GOALKEEPER";
  });
};

/** 'gk' when everyone is a GK, 'mixed' when GK + field, else 'field'. */
export const detectMode = (flags) => {
  if (!flags.length) return "field";
  const gks = flags.filter(Boolean).length;
  if (gks === 0) return "field";
  if (gks === flags.length) return "gk";
  return "mixed";
};

// ---------------------------------------------------------------------------
// row building (values, best-value markers, bar shares)
// ---------------------------------------------------------------------------

const roundFor = (v, fmt) =>
  fmt === "int" ? Math.round(v) : Math.round(v * 10) / 10;

export const formatValue = (v, fmt) => {
  if (v === null || v === undefined) return EM_DASH;
  if (fmt === "pct1") return `${(Math.round(v * 10) / 10).toFixed(1)}%`;
  if (fmt === "dec1") return (Math.round(v * 10) / 10).toFixed(1);
  // Thousands grouping through the platform's pinned formatter (apostrophe,
  // utils/currencyFormatter.js) — same convention as TransferHistory on the
  // same screen, never the viewer-/en-US-comma variant.
  return formatGroupedNumber(Math.round(v));
};

/**
 * Index of the winning value or -1. Ties (at display precision) get NO
 * marking; for `lower` metrics the lowest value wins. Values must already be
 * rounded to display precision.
 */
export const pickBestIndex = (values, lower) => {
  const nums = values.filter((v) => v !== null && v !== undefined);
  if (nums.length < 2) return -1;
  const best = lower ? Math.min(...nums) : Math.max(...nums);
  if (nums.filter((v) => v === best).length > 1) return -1;
  return values.indexOf(best);
};

const clampShare = (x) => Math.max(0.05, Math.min(1, x));

/** Relative bar share per value (0.05–1); null for missing values. */
export const barShares = (values, lower) => {
  const nums = values.filter((v) => v !== null && v !== undefined);
  const max = nums.length ? Math.max(...nums) : 0;
  // Lower-is-better: the minimum over ALL values (0 included) is the best
  // case. v === 0 gets the full bar; a positive v scales as min/v, so against
  // a best of 0 it collapses to the 0.05 floor instead of both values getting
  // full bars (e.g. Big chances missed [0, 8]).
  const min = nums.length ? Math.min(...nums) : 0;
  return values.map((v) => {
    if (v === null || v === undefined) return null;
    const share = lower ? (v > 0 ? min / v : 1) : max > 0 ? v / max : 0;
    return clampShare(share);
  });
};

const effectiveValue = (def, statMap, per90Mode) => {
  if (per90Mode && def.per90able && def.statKey) return per90Of(def.statKey)(statMap);
  return def.value(statMap);
};

const effectiveFmt = (def, per90Mode) =>
  per90Mode && def.per90able ? "dec1" : def.fmt;

/**
 * Build one grid row: per-player cells with display text, companion %,
 * best-value flag and bar share. `statMaps[i]` is null for a player without
 * stats for the selected season (whole cell renders "—", no bar).
 */
export const buildRow = (def, statMaps, per90Mode = false) => {
  const fmt = effectiveFmt(def, per90Mode);
  const raw = statMaps.map((s) => (s ? effectiveValue(def, s, per90Mode) : null));
  const rounded = raw.map((v) => (v === null || v === undefined ? null : roundFor(v, fmt)));
  const bestIdx = pickBestIndex(rounded, def.lower);
  const shares = barShares(rounded, def.lower);
  return {
    id: def.id,
    label: def.label,
    note: def.note || "",
    extra: !!def.extra,
    cells: raw.map((v, i) => {
      if (v === null || v === undefined) {
        return { missing: true, text: EM_DASH, sub: "", isBest: false, share: null };
      }
      const subRaw = def.sub && statMaps[i] ? def.sub(statMaps[i]) : null;
      return {
        missing: false,
        text: formatValue(v, fmt),
        sub: subRaw === null || subRaw === undefined ? "" : `${Math.round(subRaw)}%`,
        isBest: i === bestIdx,
        share: shares[i],
      };
    }),
  };
};

/**
 * Rating hero cells. `statMaps[i]` null -> empty column (dashed "No
 * statistics" card). Stats present but rating NULL (backend defect F1, e.g.
 * SportMonks entries) -> `missing`: the hero shows "—" without a bar — NEVER
 * the "No statistics" card, the player demonstrably has statistics.
 * Bar scale follows the design prototype: baseline min-0.3, floor 18%.
 */
export const buildHero = (statMaps) => {
  const values = statMaps.map((s) =>
    s ? computePerMatchRating(s.rating, s.court) : null,
  );
  const nums = values.filter((v) => v !== null);
  const bestIdx = pickBestIndex(values, false);
  const max = nums.length ? Math.max(...nums) : 1;
  const base = (nums.length ? Math.min(...nums) : 0) - 0.3;
  return values.map((v, i) => {
    if (!statMaps[i]) {
      return { empty: true, missing: false, text: "", isBest: false, share: null };
    }
    if (v === null) {
      return { empty: false, missing: true, text: EM_DASH, isBest: false, share: null };
    }
    const share = max > base ? (v - base) / (max - base) : 1;
    return {
      empty: false,
      missing: false,
      text: v.toFixed(1),
      isBest: i === bestIdx,
      share: Math.max(0.18, Math.min(1, share)),
    };
  });
};
